/**
 * Scoping rule for the multi-tenant `X-Firm-Id` header.
 *
 * The firm context is only meaningful to our own firm-workspace backend
 * (``environment.baseUrl``). Stamping it on cross-origin calls to other
 * services — notably the firm-agnostic Python search-rag API, which is scoped
 * by ``X-User-Id`` — makes the browser send a custom request header those
 * services don't list in ``Access-Control-Allow-Headers``, so their CORS
 * preflight fails and the request is blocked.
 *
 * Keeping this a pure predicate lets the interceptor stay a one-liner and lets
 * the boundary be unit-tested without an HTTP harness.
 */

/** Strip a single trailing slash so origin comparison is robust. */
const stripTrailingSlash = (value: string): string => value.replace(/\/$/, '');

/** True when `requestUrl` should carry the `X-Firm-Id` header for the given base(s). */
export function shouldAttachFirmHeader(
  requestUrl: string,
  firmBackendBaseUrl: string | string[],
): boolean {
  const url = (requestUrl || '').trim();
  if (!url) return false;

  // Relative URL → resolved against the app origin, not a third-party host.
  const isAbsolute = /^https?:\/\//i.test(url);
  if (!isAbsolute) return true;

  const bases = (Array.isArray(firmBackendBaseUrl) ? firmBackendBaseUrl : [firmBackendBaseUrl])
    .map((b) => stripTrailingSlash((b || '').trim()))
    .filter((b) => b.length > 0);

  return bases.some((base) => url === base || url.startsWith(`${base}/`));
}
